const PREVIEW_SUMMARY_DEFINITION = [
  ['Rows in file', ['totalRows'], 'gray'],
  ['Will enter campaign', ['createRows'], 'green'],
  ['Will not enter campaign', ['duplicateRows', 'skippedRows', 'errorRows'], 'amber'],
  ['Needs review', ['errorRows'], 'red'],
];

const RESULT_SUMMARY_DEFINITION = [
  ['Imported', ['createdRows', 'updatedRows'], 'green'],
  ['Campaign started', ['campaign.startedCount', 'outreach.startedRows'], 'green'],
  ['Skipped', ['campaign.skippedCount', 'skippedRows'], 'amber'],
  ['Needs attention', ['campaign.failedCount', 'errorRows'], 'red'],
];

const ACTION_LABELS = {
  create: 'Will create',
  created: 'Created',
  skip_duplicate: 'Duplicate',
  error: 'Needs fix',
};

const SKIP_REASON_COPY = {
  missing_phone: {
    label: 'Missing phone',
    meaning: 'No usable phone number was available.',
    action: 'View needs attention',
    href: '/needs-attention?reason=missing_phone',
  },
  duplicate_applicant: {
    label: 'Duplicate applicant',
    meaning: 'Matched an existing applicant; no new campaign was started.',
    action: 'View applicants',
    href: '/',
  },
  opt_out_or_do_not_contact: {
    label: 'Opted out / do not contact',
    meaning: 'Contact is blocked by consent or manual flag.',
    action: 'View applicants',
    href: '/',
  },
  validation_error: {
    label: 'Validation error',
    meaning: 'The row could not be safely imported.',
    action: 'View import details',
    href: '#import-preview-rows',
  },
  already_active: {
    label: 'Already active',
    meaning: 'The applicant is already in an active workflow.',
    action: 'View applicants',
    href: '/',
  },
  terminal_stage: {
    label: 'Terminal stage',
    meaning: 'The applicant is already in a final workflow state.',
    action: 'View applicants',
    href: '/',
  },
  import_row_skipped: {
    label: 'Import row skipped',
    meaning: 'The row was skipped and no campaign was started.',
    action: 'View import details',
    href: '#import-preview-rows',
  },
  first_contact_failed: {
    label: 'Text failed',
    meaning: 'Campaign started but a message failed.',
    action: 'View needs attention',
    href: '/needs-attention?reason=failed_text',
  },
};

function readValue(summary, key) {
  return key.split('.').reduce((current, part) => current?.[part], summary);
}

function sumValues(summary, keys, label) {
  const values = keys.map(key => readValue(summary, key)).filter(value => value != null);
  if (values.length === 0) return 0;
  const hasNestedFallback = keys.some(key => key.includes('.'));
  if (!hasNestedFallback || label === 'Imported' || label === 'Needs attention') {
    return values.reduce((total, value) => total + Number(value || 0), 0);
  }
  return Number(values[0] || 0);
}

export function buildCandidateImportSummaryCards(summary = {}) {
  const isResult = Boolean(summary?.campaign || summary?.outreach || summary?.createdRows != null);
  const definition = isResult ? RESULT_SUMMARY_DEFINITION : PREVIEW_SUMMARY_DEFINITION;
  return definition.map(([label, keys, tone]) => ({
    label,
    value: sumValues(summary, keys, label),
    tone,
  }));
}

export function getPreviewImportEligibility(preview) {
  if (!preview) {
    return { canImport: false, reason: 'Preview a CSV before importing.' };
  }

  if ((preview.parseErrors || []).length > 0) {
    return { canImport: false, reason: 'Fix CSV header errors before importing.' };
  }

  if (Number(preview.createRows || 0) < 1) {
    return { canImport: false, reason: 'No new candidates are ready to import.' };
  }

  return { canImport: true, reason: '' };
}

export function getCandidateImportActionLabel(action) {
  return ACTION_LABELS[action] || 'Review';
}

export function getCandidateImportStatus(row = {}) {
  if ((row.errors || []).length > 0 || row.action === 'error') {
    return { label: (row.errors || [])[0] || 'Row has validation errors', tone: 'red' };
  }

  if (row.action === 'skip_duplicate') {
    const matchType = row.duplicate?.matchType;
    return { label: matchType ? `Duplicate by ${matchType}` : 'Duplicate candidate', tone: 'amber' };
  }

  if (row.action === 'created') {
    return { label: 'Applicant created', tone: 'green' };
  }

  return { label: 'Ready', tone: 'green' };
}

export function getCandidateCampaignBadge(row = {}) {
  if ((row.errors || []).length > 0 || row.action === 'error') {
    if ((row.errors || []).some(error => /phone/i.test(String(error)))) {
      return { label: 'Missing phone — skipped', tone: 'amber' };
    }
    return { label: 'Validation error', tone: 'red' };
  }
  if (row.action === 'skip_duplicate') return { label: 'Duplicate — no campaign', tone: 'amber' };
  if (row.outreach?.status === 'failed') return { label: 'Text failed', tone: 'red' };
  if (row.action === 'created') return { label: 'Campaign started', tone: 'green' };
  if (row.action === 'create') return { label: 'Campaign eligible', tone: 'green' };
  return { label: 'Review required', tone: 'amber' };
}

export function buildCampaignSkipReasonRows(result = {}) {
  const reasons = Array.isArray(result?.campaign?.skipReasons) ? result.campaign.skipReasons : [];
  const rows = reasons.map((reason) => {
    const copy = SKIP_REASON_COPY[reason.key] || {
      label: reason.label || 'Skipped',
      meaning: 'No campaign was started for this reason.',
      action: 'View import details',
      href: '#import-preview-rows',
    };
    return {
      key: reason.key || copy.label,
      label: copy.label,
      count: Number(reason.count || 0),
      meaning: copy.meaning,
      action: copy.action,
      href: copy.href,
    };
  }).filter(row => row.count > 0);

  const failedCount = Number(result?.campaign?.failedCount || result?.outreach?.failedRows || 0);
  if (failedCount > 0 && !rows.some(row => row.key === 'first_contact_failed')) {
    const copy = SKIP_REASON_COPY.first_contact_failed;
    rows.push({ key: 'first_contact_failed', count: failedCount, ...copy });
  }
  return rows;
}

export function getImportResultLinks() {
  return {
    importedApplicantsHref: '/?source=direct',
    needsAttentionHref: '/needs-attention',
    importAnotherHref: '#candidate-csv-file',
  };
}

export function getCreatedApplicantLinks(result) {
  return (result?.rows || [])
    .filter(row => row.action === 'created' && row.applicantId)
    .map(row => ({
      rowNumber: row.rowNumber,
      applicantId: row.applicantId,
      href: `/applicants/${row.applicantId}`,
      label: `Row ${row.rowNumber} applicant`,
    }));
}
